/**
 * What a machine reads under the `'planEditor'` overlay (`spec-onboarding-3d-plan-edit-machines`,
 * R2.2 and R3.4): where to register the box it is drawn with, and whether it is selected or hovered
 * (`onboarding-status-colours-plan-machines`, R4); with `plan-machine-outline`, where its model's
 * outline comes from and what draws its marks on it.
 * Provided by the 3D plans' scene (`PlanScene`), for the editor and the read-only plan alike; nothing
 * else reads it.
 */
import { createContext, type ComponentType } from 'react';
import type { BufferGeometry, Object3D, Texture } from 'three';

/** A machine's drawn box, metres, in its plan item's own frame (x along its width, y its depth). */
export interface PlanMachineBox {
  width: number;
  depth: number;
  /** The box centre's offset from the item's point, in the item's own frame. */
  offsetX: number;
  offsetY: number;
  /** The model's top above the floor. */
  top: number;
  /**
   * The model's outline seen from above (`plan-machine-outline`, R1), one or more loops of three or
   * more points in the same frame as the offsets: `undefined` while it is being worked out, `null`
   * where there is none (an empty list reads as none too). Hover, selection and picking use it; the
   * box still sizes and places everything else.
   */
  outline?: readonly (readonly { x: number; y: number }[])[] | null;
}

export interface PlanMachineEntry {
  box: PlanMachineBox;
  /** The drawn model, what the pointer's ray is cast against. */
  model: Object3D;
}

/** Where the drawn machines register; one per editor scene. */
export interface PlanMachineSink {
  set(refid: string, entry: PlanMachineEntry): void;
  /** Removes the entry only while it is still this model's, so a remount's new entry stays. */
  remove(refid: string, model: Object3D): void;
}

/**
 * A model's outline as the plan's store holds it (`plan-machine-outline`, R1): being worked out, ready,
 * or none (no usable triangle, too large, or it failed). Points are `[x, z]` in the geometry's own
 * frame, metres, before the machine's mirror. Each state is one object until it changes, so a reader
 * can compare by reference.
 */
export type MachineOutlineEntry =
  | { state: 'pending' }
  | { state: 'none' }
  | {
      state: 'ready';
      loops: readonly (readonly (readonly [number, number])[])[];
      bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
      /** The signed distance to the loops, metres, positive outside (`outlineField`). */
      field: {
        data: Float32Array;
        W: number;
        H: number;
        ox: number;
        oz: number;
        cell: number;
      };
      /** The field as a texture, shared by every machine drawing this model; made on first use. */
      texture: () => Texture;
    };

/** Where a machine finds its model's outline: one store per page, handed out by the plan's scene. */
export interface MachineOutlineSource {
  /** The entry as it stands; `pending` for a geometry never requested. Never starts work. */
  peek(geometry: BufferGeometry): MachineOutlineEntry;
  /** Queues the geometry's outline once; later calls do nothing. `modelName` names it in warnings. */
  request(geometry: BufferGeometry, modelName: string): void;
  /** Called when that geometry's entry changes; returns the unsubscribe. */
  subscribe(geometry: BufferGeometry, listener: () => void): () => void;
}

/** Whether a machine wears the selection or the hover on its outline. */
export interface PlanMachineBoxMarks {
  selected: boolean;
  hovered: boolean;
}

/** What draws a machine's selection and hover on its outline (`plan-machine-outline`, R2, R3). */
export interface PlanMachineOutlineMarksProps {
  /** The box the machine is drawn with, its bounds computed: the marks' outline until it is ready. */
  hitBox: BufferGeometry;
  /** The model's outline, from the plan's store. */
  outline: MachineOutlineEntry;
  /** The model mesh's mirror in x and z (each ±1), which the outline takes as the mesh does. */
  mirror: readonly [number, number];
  marks: PlanMachineBoxMarks;
  /** The order the machine's part draws in (its group carries it); the marks draw just over it. */
  renderOrder: number;
}

export interface PlanEditorMachineState {
  registry: PlanMachineSink;
  selectedIds: ReadonlySet<string>;
  hoveredId: string | null;
  /**
   * Each machine's selection and hover, where the plan decides them from more than `selectedIds` and
   * `hoveredId` (`onboarding-status-colours-plan-machines`, R4 and R5: a search match is drawn as
   * selected, and Build floors draws neither). Absent, they come from those two.
   */
  machineAppearance?: (refid: string) => PlanMachineBoxMarks;
  /**
   * The floor's machine layer order, for the marks. three.js sorts see-through objects by their
   * nearest group's order first, so the part's own group carries this one: the marks then draw over
   * the floor's space fills and marks, not under them. Absent, the machine's render order (0 outside
   * Build floors).
   */
  linesOrder?: number;
  /**
   * What draws the machine's selection and hover, given by the plan's scene (`PlanScene`,
   * `PlanMachineOutlineMarks`). Absent, the machine draws none.
   */
  OutlineMarks?: ComponentType<PlanMachineOutlineMarksProps>;
  /**
   * The page's outline store (`machineOutlineStore`), given by the plan's scene. Absent, a machine
   * has no outline and is drawn and picked by its box.
   */
  outlines?: MachineOutlineSource;
}

export const PlanEditorMachineContext = createContext<PlanEditorMachineState | null>(null);
